import React from "react";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Sparkles, Brain, Flame, Sun, Baby, HeartHandshake, ArrowRight, Compass, Feather, Smile } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };
const stagger = { show: { transition: { staggerChildren: 0.12 } } };

const SPOTLIGHT = [
  {
    icon: Brain, color: "#2C6BAC", tag: "Neurodivergent workers",
    title: "Attention that wanders is not a flaw. It just needs a way home.",
    body: "For people with ADHD and other attention-deficit profiles, the hard part is rarely the work - it is the return. WYLO's refocus prompt is a gentle 'here is where you were', which turns a twenty-minute scramble into a two-second glance. That is inclusion in practice: autonomy, independence, and no need to explain yourself.",
    perks: ["Refocus without asking anyone", "Own your own data, always", "Hotkey and voice-over on the way"],
  },
  {
    icon: Flame, color: "#E24B4A", tag: "Burnout recovery",
    title: "Coming back to work should feel like a ramp, not a cliff.",
    body: "Charities supporting people recovering from burnout use WYLO to make re-entry gentler: fewer dropped threads, less rework, and an honest picture of where the day is getting fragmented - so support can be asked for early, with evidence, rather than late, in crisis.",
    perks: ["Spot overload trends early", "Advocate with your own numbers", "No surveillance, ever"],
  },
  {
    icon: Sun, color: "#BA7517", tag: "Brain fog & menopause",
    title: "Brain fog is real. Losing your place shouldn't cost you your confidence.",
    body: "Women navigating menopause describe the same thing again and again: walking back to the desk and blanking. WYLO holds the thread so you don't have to. Charities use it to keep experienced, brilliant people in roles they love.",
    perks: ["Context restored instantly", "Private by design", "Works with the tools you already use"],
  },
  {
    icon: Baby, color: "#1D9E75", tag: "New parents",
    title: "Postpartum memory is doing its best. WYLO covers the gaps.",
    body: "Returning after parental leave, on little sleep, with a memory that is quietly rewiring itself - that is when a calm 'you were here' matters most. Charities supporting new mothers use WYLO to make the return to work feel possible, then normal, then good.",
    perks: ["Glanceable, not noisy", "Fits around interruptions (there will be many)", "Independence, not oversight"],
  },
];

const FLOATERS = [
  { Icon: Feather, x: "8%", y: "18%", delay: 0 },
  { Icon: Smile, x: "88%", y: "22%", delay: 1.2 },
  { Icon: Compass, x: "14%", y: "72%", delay: 0.6 },
  { Icon: Sparkles, x: "84%", y: "70%", delay: 1.8 },
];

export default function Charities() {
  return (
    <MarketingLayout>
      <section className="relative overflow-hidden pt-28 pb-24 bg-gradient-to-b from-[#FFF8EC] via-[#F8F9FC] to-[#F8F9FC]" data-testid="charities-hero">
        {FLOATERS.map(({ Icon, x, y, delay }, i) => (
          <motion.div key={i} className="absolute hidden md:block text-[#2C6BAC]/30" style={{ left: x, top: y }}
            animate={{ y: [0, -14, 0], rotate: [0, 6, 0] }} transition={{ duration: 5, repeat: Infinity, delay, ease: "easeInOut" }}>
            <Icon size={36} />
          </motion.div>
        ))}
        <motion.div className="max-w-4xl mx-auto px-6 lg:px-10 text-center" initial="hidden" animate="show" variants={stagger}>
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#BA7517] font-semibold mb-4 inline-flex items-center gap-2">
            <Sparkles size={14} /> Using WYLO · Charities
          </motion.p>
          <motion.h1 variants={fadeUp} className="font-display text-5xl lg:text-7xl tracking-tighter text-[#1A1A2E] leading-[0.95]">
            Everyone deserves a way <span className="text-[#2C6BAC]">back to their brilliant.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-7 text-lg text-[#555568] leading-relaxed max-w-2xl mx-auto">
            WYLO was built for the moment after the interruption. For many of the people charities support, that moment is
            the whole challenge. We would love to help you help them - with a tool that hands people their place back,
            and never watches over their shoulder.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-8" data-testid="charities-communities">
            <p className="text-sm font-medium text-[#1A1A2E]">We'd be happy to partner with Charities supporting these communities</p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {SPOTLIGHT.map((s, i) => (
                <a key={s.tag} href={`#charity-${i}`} title={s.tag} aria-label={s.tag} data-testid={`charities-community-${i}`}
                  className="group flex flex-col items-center gap-1.5">
                  <span className="h-12 w-12 rounded-xl flex items-center justify-center text-white transition-transform group-hover:-translate-y-1 group-hover:rotate-[-6deg]" style={{ backgroundColor: s.color }}>
                    <s.icon size={22} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: s.color }}>{s.tag}</span>
                </a>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="mailto:support@usewylo.com?subject=WYLO%20for%20our%20charity" data-testid="charities-cta-email">
              <Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6">Partner with us <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </a>
          </motion.div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-6 pb-24 space-y-8">
        {SPOTLIGHT.map((s, i) => (
          <motion.article key={i} id={`charity-${i}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }} data-testid={`charities-spotlight-${i}`}
            className={`scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center rounded-2xl border border-[#E8ECF2] bg-white p-8 lg:p-10 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <div className="lg:col-span-4 flex flex-col items-start">
              <motion.div className="h-16 w-16 rounded-2xl flex items-center justify-center text-white" style={{ backgroundColor: s.color }}
                whileHover={{ rotate: -8, scale: 1.05 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}>
                <s.icon size={28} />
              </motion.div>
              <p className="mt-4 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: s.color }}>{s.tag}</p>
              <ul className="mt-4 space-y-2">
                {s.perks.map((p) => (
                  <li key={p} className="text-sm text-[#1A1A2E] flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.color }} /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-2xl lg:text-4xl tracking-tighter text-[#1A1A2E] leading-[1.1]">{s.title}</h2>
              <p className="mt-4 text-[#555568] leading-relaxed">{s.body}</p>
            </div>
          </motion.article>
        ))}
      </section>

      <section className="bg-[#1A3A5C] text-white overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <motion.div className="md:col-span-3 flex justify-center" animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
            <div className="h-24 w-24 rounded-full bg-[#7DE3B4]/20 flex items-center justify-center"><HeartHandshake size={40} className="text-[#7DE3B4]" /></div>
          </motion.div>
          <div className="md:col-span-9">
            <p className="text-xs uppercase tracking-[0.2em] text-[#7DE3B4] font-semibold mb-3">For charities and their people</p>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tighter leading-[1.05]">Inclusion, autonomy, independence - and a bit of joy.</h2>
            <p className="mt-4 text-[#E8ECF2]/85 leading-relaxed">
              We offer charity pricing, onboarding help for support workers, and a promise: WYLO will always give people their
              place back without giving anyone else their data. If you support neurodivergent workers, people recovering from
              burnout, or anyone navigating brain fog, let's talk.
            </p>
            <a href="mailto:support@usewylo.com?subject=WYLO%20for%20our%20charity" className="mt-6 inline-flex" data-testid="charities-cta-email-bottom">
              <Button className="bg-white text-[#1A3A5C] hover:bg-[#E8ECF2] h-11 px-6">Say hello <Smile size={16} className="ml-2" /></Button>
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
